import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, Injector, input, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, FormsModule, ReactiveFormsModule, NG_VALUE_ACCESSOR, NgControl } from '@angular/forms';
import { NeoFormFieldModule } from '@neogov/pattern-library/neo-form-field';
import { NeoInputModule } from '@neogov/pattern-library/neo-input';
import { NeoDatepickerV2Module } from '@neogov/pattern-library/neo-datepicker-v2';
import { IconComponent } from '../icon/icon.component';
import {
  FieldErrorsComponent,
  OuterControlErrorStateMatcher,
  shouldSurfaceFieldError,
} from '../field-errors';

@Component({
  selector: 'cm-date-field',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NeoFormFieldModule,
    NeoInputModule,
    NeoDatepickerV2Module,
    IconComponent,
    FieldErrorsComponent,
  ],
  templateUrl: './date-field.component.html',
  styleUrl: './date-field.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: DateFieldComponent,
      multi: true,
    },
  ],
})
export class DateFieldComponent implements ControlValueAccessor {
  public readonly label = input<string>();
  public readonly placeholder = input<string>('mm/dd/yyyy');
  public readonly autId = input.required<string>();
  public readonly hideMessages = input<boolean>(false);
  public readonly hint = input<string>('');
  public readonly required = input<boolean>(false);

  // Error handling inputs
  public readonly errorMessages = input<Partial<Record<string, string>>>({});
  public readonly showErrorsWhen = input<'touched' | 'dirty' | 'always'>('touched');
  public readonly hideErrors = input<boolean>(false);

  public value: Date | null = null;
  public disabled = false;

  private readonly _injector = inject(Injector);
  private readonly _cdr = inject(ChangeDetectorRef);
  public readonly ngControl = signal<NgControl | null>(null);

  /**
   * Feeds the OUTER control's validity into the native input so the pattern
   * library exposes aria-invalid and links the error via aria-describedby
   * (WCAG 3.3.1 / 4.1.2). See {@link OuterControlErrorStateMatcher}.
   */
  protected readonly errorStateMatcher = new OuterControlErrorStateMatcher(() =>
    shouldSurfaceFieldError(this.ngControl()?.control, this.showErrorsWhen(), this.hideErrors())
  );

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  public onChange: (value: Date | null) => void = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  public onTouched: () => void = () => {};

  constructor() {
    // Defer NgControl lookup to avoid circular dependency
    // The NgControl directive will be available after the component is constructed
    queueMicrotask(() => {
      try {
        const ngControl = this._injector.get(NgControl, null, { optional: true, self: true }) || null;
        this.ngControl.set(ngControl);
      } catch {
        this.ngControl.set(null);
      }
    });
  }

  public writeValue(value: Date | null): void {
    this.value = value;
    this._cdr.markForCheck();
  }

  public registerOnChange(fn: (value: Date | null) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  public onDateChange(value: Date | null): void {
    this.value = value;
    this.onChange(this.value);
  }

  public onBlur(): void {
    // Simply call onTouched - no setTimeout needed!
    // The cm-field-errors component is subscribed to control.events
    // which emits TouchedChangeEvent synchronously when markAsTouched() is called
    this.onTouched();
  }
}
